import React, { useEffect } from "react"

import { useNavigate } from "react-router-dom"

import { FixedTopRightActionElement } from "@front-shared/elements/fixedTopRightAction.element.tsx"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook.ts"

import { LoginStep } from "../../../config/auth.config"
import { GENERAL_CONFIG } from "../../../config/general.config"
import { ROUTES_CONFIG } from "../../../config/routes.config"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { authSelector } from "../../../store/selectors/auth.selector"
import {
    authSetLoginData,
    authSetLoginStep,
} from "../../../store/slices/auth.slice"
import { EmailStep } from "./steps/email.step"
import { MfaSelectionStep } from "./steps/mfaSelection.step"
import { PasswordStep } from "./steps/password.step"
import { PhoneNumberStep } from "./steps/phoneNumber.step"
import { SMSCodeStep } from "./steps/smsCode.step"
import { TOTPCodeStep } from "./steps/totpCode.step"
import { TOTPSetupStep } from "./steps/totpSetup.step"

export const LoginComponent: React.FunctionComponent = () => {
    useScrollFix()

    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    const authState = useAppSelector(authSelector)

    // Init & cleanup
    useEffect(() => {
        dispatch(authSetLoginStep(LoginStep.EMAIL))

        return () => {
            dispatch(authSetLoginData(null))
        }
    }, [])

    const getLoginStep = () => {
        switch (authState.loginStep) {
            case LoginStep.EMAIL:
                return <EmailStep />

            case LoginStep.PASSWORD:
                return <PasswordStep />

            case LoginStep.MFA_SELECTION:
                return <MfaSelectionStep />

            case LoginStep.MFA_PHONE_COLLECTION:
                return <PhoneNumberStep />

            case LoginStep.MFA_SMS_CODE_COLLECTION:
                return <SMSCodeStep />

            case LoginStep.MFA_TOTP_SETUP:
                return <TOTPSetupStep />

            case LoginStep.MFA_TOTP_CODE_COLLECTION:
                return <TOTPCodeStep />
        }
    }

    const getTopRightAction = () => {
        switch (authState.loginStep) {
            case LoginStep.EMAIL:
            case LoginStep.PASSWORD:
                return (
                    <FixedTopRightActionElement
                        ctaAction={() => navigate(ROUTES_CONFIG.registerUrl)}
                        ctaLabel="Create an Account"
                        additionalLabel="Don’t have a login?"
                        className="z-201 md:!static md:mt-[32px]"
                    />
                )
        }
    }

    return (
        <div className="md:px-[16px]">
            <PageHelmetElement
                title="Log In"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            {getLoginStep()}

            {getTopRightAction()}
        </div>
    )
}
